import Vue from 'vue'
import dataBrowserResource from '@/resources/dataBrowser'
import { createNamespacedHelpers } from 'vuex'
const { mapActions } = createNamespacedHelpers('day6CharacterBrowser')

const mixin = {
  data: function () {
    return {
      // Animations
      animationGroupId: '',
      activePoseSequenceId: '',
      characterAnimationsCache: {},
      isLoadingAnimations: false,
      isLoadingPoseSequence: false,
      isSavingNewPoseSequence: false,
      isSavingPoseSequenceName: false,
      isDeletingPoseSequence: false,
      // Only use blank string for indicating new character
      poseSequenceNameChangingId: null,
      poseSequenceDeletingId: null,
      // Loading
      loadingPoseSequenceData: null,
      isSmallScreenAnimation: false
    }
  },
  computed: {
    isUpdatingAnimation () {
      return this.isLoadingAnimations ||
          this.isSavingNewPoseSequence ||
          this.isSavingPoseSequenceName ||
          this.isDeletingPoseSequence ||
          this.isLoadingPoseSequence
    }
  },
  methods: {
    ...mapActions([
      'queryCharacterAnimations',
      'queryPoseSequence',
      'saveActivePoseSequence',
      'savePoseSequenceName',
      'deletePoseSequence'
    ]),

    resetAnimationData () {
      this.animationGroupId = ''
      this.characterAnimationsCache = {}
      this.activePoseSequenceId = ''
      this.isSmallScreenAnimation = false
    },

    noModalOpenPoseSequences (modalKey) {
      if (!modalKey) {
        this.closeModalOpenPoseSequences()
        return
      }

      this.confirmCloseModalDialog(modalKey, this.closeModalOpenPoseSequences)
    },

    closeModalOpenPoseSequences () {
      // Assume modal was closed. Avoid circular query to Unity and back
      this.$refs.poseSequencesView.openPoseSequences()
    },

    onOpenPoseSequences () {
      this.isEstimatingPose = false

      this._openModalCallback = this.noModalOpenPoseSequences
      this.unityQueryOpenModal()
    },

    async loadCharacterAnimations () {
      if (!this.isAccountPaid) { return }

      // TODO Save if changes?

      if (this.activeDimensionId) {
        this.isLoadingAnimations = true
        this.runInLoadingDialog(async () => {
          const { activeSequence, sequences } = await this.queryCharacterAnimations(this.activeDimensionId)
          this.characterAnimationsCache = sequences
          if (activeSequence in sequences) {
            this.activePoseSequenceId = activeSequence
          } else {
            // In case data is inconsistent on the backend
            const sequenceIds = Object.keys(sequences)
            sequenceIds.sort()
            this.activePoseSequenceId = sequenceIds.length ? sequenceIds[0] : ''
          }

          this.loadActivePoseSequence()
        },
        '',
        () => { this.isLoadingAnimations = false })
      } else {
        this.resetAnimationData()
      }
    },
    async loadActivePoseSequence () {
      if (!this.isAccountPaid) { return }

      const poseSequenceId = this.activePoseSequenceId
      if (!poseSequenceId || this.isLoadingPoseSequence) { return }

      let poseSequenceData
      this.isLoadingPoseSequence = true
      // Don't run in loading dialog. Too much to manage all different loading dialogs
      try {
        const { id, data } = await this.queryPoseSequence(poseSequenceId)
        if (!(data && id === this.activePoseSequenceId)) { return }

        this.unityLoadPoseSequence(data)
        this.resetPoseCounters()

        poseSequenceData = data
      } finally {
        this.isLoadingPoseSequence = false
      }

      this.notifyAnimationOnSmallScreen(poseSequenceData)
    },

    notifyAnimationOnSmallScreen (poseSequenceData) {
      this.isSmallScreenAnimation = false

      const poseSequencesView = this.$refs.poseSequencesView
      if (!(poseSequenceData && this.isSmallScreen && poseSequencesView)) { return }

      this.isSmallScreenAnimation = poseSequenceData.sequences || poseSequenceData.locomotionParameters

      if (this.isSmallScreenAnimation) {
        this.noSavingSmallScreenAnimationsDialog()
      }
    },

    onLoadPoseSequence (sequenceData) {
      if (!sequenceData.id) { return }

      this.loadingPoseSequenceData = sequenceData

      if (this.isPoseChanged) {
        this.loadPoseSaveChangesDialog()
      } else {
        this.continueLoadingPoseSequence()
      }
    },
    async saveChangesLoadPoseSequence () {
      await this.saveChanges()
      await this.continueLoadingPoseSequence()
    },
    async continueLoadingPoseSequence () {
      if (!this.loadingPoseSequenceData) { return }
      const { id: loadingId, name: sequenceName } = this.loadingPoseSequenceData
      if (!loadingId || loadingId === this.activePoseSequenceId) { return }
      if (this.isLoadingPoseSequence) { return }

      this.isLoadingPoseSequence = true
      const loadingMessage = dataBrowserResource.loadingPoseSequenceMessage(sequenceName)
      const poseSequenceData = await this.runInLoadingDialog(async () => {
        const { id, data } = await this.queryPoseSequence(loadingId)
        if (data) {
          let isActive = this.activePoseSequenceId === id
          if (!isActive) {
            // Loading was cancelled
            if (id !== loadingId) { return }

            isActive = await this.saveActivePoseSequence({
              idAnimationGroup: this.animationGroupId,
              idSequence: id
            })
          }

          if (isActive) {
            this.unityLoadPoseSequence(data)
            // TODO The following assume Unity loads the animation successfully
            this.activePoseSequenceId = id
            this.resetPoseCounters()
          } else {
            this.loadPoseSequenceFailDialog()
          }
        }
        return data
      },
      loadingMessage,
      () => {
        this.loadingPoseSequenceData = null
        this.isLoadingPoseSequence = false
      })

      this.notifyAnimationOnSmallScreen(poseSequenceData)
    },

    /**
     * Updates temporary pose sequence with newly saved pose sequence ID
     */
    _updateTemporaryPoseSequence () {
      const activeSequenceId = this.activePoseSequenceId
      if (!activeSequenceId) {
        Vue.logger.error(new Error('Temporary pose sequence update attempted when active sequence is unsaved'))
      }

      const cache = this.characterAnimationsCache
      if ('' in cache) {
        if (!(activeSequenceId in cache)) {
          cache[activeSequenceId] = cache['']
        }

        delete cache['']
      }
    },

    async onNewPoseSequence () {
      if (this.isSavingNewPoseSequence) { return }

      // Save empty/blank/default pose if exists
      if (!this.activePoseSequenceId) {
        this.poseChangeCounter = this.poseSaveCounter + 1
        this.isSavingNewPoseSequence = true
        try {
          await this.saveDirty()
          this._updateTemporaryPoseSequence()
          this.$refs.poseSequencesView.syncState()
        } finally {
          this.isSavingNewPoseSequence = false
        }
      }

      if (this.isPoseChanged) {
        this.newPoseSaveChangesDialog()
      } else {
        this.continueNewPoseSequence()
      }
    },
    async saveChangesNewPoseSequence () {
      await this.saveChanges()
      await this.continueNewPoseSequence()
    },
    async continueNewPoseSequence (force) {
      if (!force && !this.activePoseSequenceId) {
        Vue.logger.error(new Error('New pose sequence with unsaved existing sequence encountered'))
        return
      }
      if (this.isSavingNewPoseSequence) { return }

      this.isSavingNewPoseSequence = true
      try {
        const poseSequencesView = this.$refs.poseSequencesView
        const poseSequenceName = poseSequencesView.getUniqueName()

        this.characterAnimationsCache[''] = { name: poseSequenceName }
        this.activePoseSequenceId = ''
        this.poseChangeCounter = this.poseSaveCounter + 1
        this.unityForceClearAnimation()
        await this.saveDirty()

        this._updateTemporaryPoseSequence()
        poseSequencesView.syncState()
        this.resetPoseCounters()
      } finally {
        this.isSavingNewPoseSequence = false
      }
    },

    async onUpdatePoseSequenceName ({ id, name }) {
      // Update single name at a time for easier management
      if (this.isSavingNewPoseSequence) { return }

      this.isSavingPoseSequenceName = true
      this.poseSequenceNameChangingId = id
      try {
        const isNewPoseSequence = !(id && this.animationGroupId && this.activePoseSequenceId)
        if (isNewPoseSequence) {
          this.poseChangeCounter = this.poseSaveCounter + 1
        }
        if (this.hasUnsavedChanges) {
          await this.saveDirty()

          if (isNewPoseSequence) {
            this._updateTemporaryPoseSequence()
            id = this.activePoseSequenceId
          }
        }

        if (!(id && this.animationGroupId)) {
          Vue.logger.error(new Error('Animation group and/or sequence does not exist when updating sequence name'))
          return
        }

        await this.syncPoseSequenceName(this.animationGroupId, id, name)
      } finally {
        this.isSavingPoseSequenceName = false
        this.poseSequenceNameChangingId = null
      }
    },
    async syncPoseSequenceName (idAnimationGroup, idSequence, name, isNewSequence) {
      const sequenceData = await this.savePoseSequenceName({
        idAnimationGroup,
        idSequence,
        name
      })
      if (idSequence in sequenceData &&
          (idSequence in this.characterAnimationsCache || isNewSequence)) {
        const cacheId = idSequence in this.characterAnimationsCache ? idSequence : ''
        this.characterAnimationsCache[cacheId] = sequenceData[idSequence]
        if (!this.activePoseSequenceId) {
          this.activePoseSequenceId = idSequence
          this._updateTemporaryPoseSequence()
        }
        this.$refs.poseSequencesView.syncState()
      } else {
        Vue.logger.error(new Error('Unexpected update pose sequence name result'))
      }
    },

    async onDeletePoseSequence (idDelete) {
      const idAnimationGroup = this.animationGroupId
      if (!idAnimationGroup) { return }

      if (this.isDeletingPoseSequence) { return }

      this.isDeletingPoseSequence = true
      this.poseSequenceDeletingId = idDelete
      try {
        const isDeleted = await this.deletePoseSequence({
          idAnimationGroup,
          idSequence: idDelete
        })
        if (isDeleted) {
          const isActiveSequenceDeleted = idDelete === this.activePoseSequenceId
          const poseSequencesView = this.$refs.poseSequencesView

          // Get next active ID before updating cache
          const activeSequenceId = isActiveSequenceDeleted ? poseSequencesView.getAfterDeleteId(idDelete) : ''
          delete this.characterAnimationsCache[idDelete]

          if (isActiveSequenceDeleted) {
            if (activeSequenceId) {
              this.loadingPoseSequenceData = { id: activeSequenceId }
              await this.continueLoadingPoseSequence()
            } else {
              await this.continueNewPoseSequence(true)
            }
            this.resetPoseCounters()
          }

          poseSequencesView.syncState()
        } else {
          this.deletePoseSequenceFailDialog()
        }
      } finally {
        this.isDeletingPoseSequence = false
        this.poseSequenceDeletingId = null
      }
    }
  }
}

export default mixin
